import { useState, useEffect } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Loading } from "../../components/Loading";
import { ErrorPageDetails } from "../../components/ErrorPageDetails";
import DetailsLabel from "../../components/DetailsLabel";
import StatusBadge from "../../components/StatusBadge";
import { DATE_TIME_FORMAT } from "../../utils/constants";
import {
  getRefundRequestById,
  approveRefundRequest,
  rejectRefundRequest,
  RefundRequestStatus,
  RefundReasonLabels,
} from "../../services/refundRequest.service";

const InnerContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flexWrap: "wrap",
});

const RowContainer = styled(Box)({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  marginBlock: "16px",
  padding: 0,
});

const ActionContainer = styled(Box)({
  display: "flex",
  gap: "16px",
  marginTop: "24px",
});

const RefundRequestView = () => {
  const { id } = useParams();
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [resolutionNote, setResolutionNote] = useState("");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["refundRequest", id],
    queryFn: () => getRefundRequestById(id),
  });

  const {
    mutate: approve,
    isPending: isApproving,
    isSuccess: isApproveSuccess,
    isError: isApproveError,
  } = useMutation({
    mutationKey: ["approveRefundRequest", id],
    mutationFn: () => approveRefundRequest(id, resolutionNote),
  });

  const {
    mutate: reject,
    isPending: isRejecting,
    isSuccess: isRejectSuccess,
    isError: isRejectError,
  } = useMutation({
    mutationKey: ["rejectRefundRequest", id],
    mutationFn: () => rejectRefundRequest(id, resolutionNote),
  });

  useEffect(() => {
    if (isApproveSuccess) {
      alert("Refund request approved successfully");
      setOpenApproveDialog(false);
      setResolutionNote("");
      refetch();
    }
  }, [isApproveSuccess, refetch]);

  useEffect(() => {
    if (isRejectSuccess) {
      alert("Refund request rejected successfully");
      setOpenRejectDialog(false);
      setResolutionNote("");
      refetch();
    }
  }, [isRejectSuccess, refetch]);

  useEffect(() => {
    if (isApproveError) {
      alert("Error approving refund request");
    }
  }, [isApproveError]);

  useEffect(() => {
    if (isRejectError) {
      alert("Error rejecting refund request");
    }
  }, [isRejectError]);

  const handleApproveClick = () => {
    setResolutionNote("");
    setOpenApproveDialog(true);
  };

  const handleRejectClick = () => {
    setResolutionNote("");
    setOpenRejectDialog(true);
  };

  const handleApproveConfirm = () => {
    approve();
  };

  const handleRejectConfirm = () => {
    reject();
  };

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !data) {
    return <ErrorPageDetails text="Error loading refund request" />;
  }

  const isPending = data.status === RefundRequestStatus.Pending;

  return (
    <Container component="main">
      <InnerContainer>
        <Typography component="h1" variant="h4" gutterBottom>
          Refund Request Details
        </Typography>

        <RowContainer>
          <Box sx={{ flex: 1, minWidth: "300px" }}>
            <Typography component="h2" variant="h5" gutterBottom>
              Request Information
            </Typography>

            <DetailsLabel label="Request ID" value={data.id} />

            <DetailsLabel label="Transaction ID" value={data.transactionId} />

            <DetailsLabel label="Credits" value={data.credits} />

            <DetailsLabel
              label="Reason"
              value={RefundReasonLabels[data.reason] || data.reason}
            />

            <DetailsLabel
              label="Description"
              value={data.description || "N/A"}
            />

            <DetailsLabel
              label="Status"
              value={<StatusBadge status={data.status} />}
            />

            <DetailsLabel
              label="Created At"
              value={format(new Date(data.createdAt), DATE_TIME_FORMAT)}
            />

            <DetailsLabel
              label="Updated At"
              value={format(new Date(data.updatedAt), DATE_TIME_FORMAT)}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: "300px", ml: { xs: 0, md: 4 } }}>
            <Typography component="h2" variant="h5" gutterBottom>
              User Information
            </Typography>

            {data.user ? (
              <>
                <DetailsLabel
                  label="User ID"
                  value={
                    <Link
                      component={RouterLink}
                      to={`/users/${data.user.id}`}
                      underline="hover"
                    >
                      {data.user.id}
                    </Link>
                  }
                />

                <DetailsLabel
                  label="Username"
                  value={data.user.username ? `@${data.user.username}` : "N/A"}
                />
              </>
            ) : (
              <DetailsLabel
                label="User"
                value={
                  data.userId ? (
                    <Link
                      component={RouterLink}
                      to={`/users/${data.userId}`}
                      underline="hover"
                    >
                      {data.userId}
                    </Link>
                  ) : (
                    "N/A"
                  )
                }
              />
            )}

            {data.transaction && (
              <>
                <Typography
                  component="h2"
                  variant="h5"
                  gutterBottom
                  sx={{ mt: 3 }}
                >
                  Transaction Information
                </Typography>

                <DetailsLabel
                  label="Transaction ID"
                  value={data.transaction.id}
                />

                <DetailsLabel label="Type" value={data.transaction.type} />

                <DetailsLabel
                  label="Credits"
                  value={data.transaction.credits}
                />
              </>
            )}
          </Box>
        </RowContainer>

        {(data.resolutionNote || data.resolvedBy || data.resolvedAt) && (
          <RowContainer>
            <Box sx={{ flex: 1 }}>
              <Typography component="h2" variant="h5" gutterBottom>
                Resolution Information
              </Typography>

              <DetailsLabel
                label="Resolution Note"
                value={data.resolutionNote || "N/A"}
              />

              <DetailsLabel
                label="Resolved By"
                value={data.resolvedBy || "N/A"}
              />

              {data.resolvedAt && (
                <DetailsLabel
                  label="Resolved At"
                  value={format(new Date(data.resolvedAt), DATE_TIME_FORMAT)}
                />
              )}
            </Box>
          </RowContainer>
        )}

        {isPending && (
          <ActionContainer>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApproveClick}
              disabled={isApproving || isRejecting}
            >
              Approve
            </Button>

            <Button
              variant="outlined"
              color="error"
              onClick={handleRejectClick}
              disabled={isApproving || isRejecting}
            >
              Reject
            </Button>
          </ActionContainer>
        )}
      </InnerContainer>

      <Dialog
        open={openApproveDialog}
        onClose={() => !isApproving && setOpenApproveDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve Refund Request</DialogTitle>

        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to approve this refund request? This will
            credit {data.credits} credits to the user&apos;s wallet.
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Resolution Note (Optional)"
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
            margin="normal"
            disabled={isApproving}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenApproveDialog(false)}
            disabled={isApproving}
          >
            Cancel
          </Button>

          <Button
            onClick={handleApproveConfirm}
            variant="contained"
            color="primary"
            disabled={isApproving}
          >
            {isApproving ? "Approving..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openRejectDialog}
        onClose={() => !isRejecting && setOpenRejectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Refund Request</DialogTitle>

        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to reject this refund request?
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Resolution Note (Optional)"
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
            margin="normal"
            disabled={isRejecting}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenRejectDialog(false)}
            disabled={isRejecting}
          >
            Cancel
          </Button>

          <Button
            onClick={handleRejectConfirm}
            variant="contained"
            color="error"
            disabled={isRejecting}
          >
            {isRejecting ? "Rejecting..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RefundRequestView;
